/**
 * Подписки на изменения (БЗ-20): документы нормативной библиотеки по видам и разделы базы знаний. Портал только
 * предлагает набор по роли, подписка добровольная. Ниже — события: новая редакция, смена статуса, новая версия статьи.
 */
import { KB_SECTIONS, type KbEvent, type KbSubscriptions, NORM_KINDS } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

const KIND_LABEL: Record<KbEvent['kind'], string> = {
  norm_release_new: 'Новая редакция',
  norm_status_changed: 'Статус изменён',
  article_published: 'Новая версия статьи',
  article_review_due: 'Пора пересмотреть',
};

function Group({
  title,
  topics,
  on,
  toggle,
}: {
  title: string;
  topics: Array<[string, string]>;
  on: Set<string>;
  toggle: (t: string) => void;
}) {
  return (
    <fieldset className="mb-4">
      <legend className="mb-1 text-[13px] font-medium text-ink">{title}</legend>
      <div className="grid gap-x-4 md:grid-cols-2">
        {topics.map(([topic, label]) => (
          <label
            key={topic}
            className="flex min-h-11 cursor-pointer items-center gap-3 border-b border-line/60 py-1.5 text-[14px] md:min-h-9"
          >
            <input
              type="checkbox"
              className="size-5 shrink-0 md:size-4"
              checked={on.has(topic)}
              onChange={() => toggle(topic)}
            />
            <span className="[overflow-wrap:anywhere]">{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function SubscriptionsTab() {
  const qc = useQueryClient();
  const subs = useQuery({
    queryKey: ['kb', 'subs'],
    queryFn: () => api<KbSubscriptions>('/kb/subscriptions'),
  });
  const events = useQuery({ queryKey: ['kb', 'events'], queryFn: () => api<KbEvent[]>('/kb/events') });
  const [on, setOn] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (subs.data) setOn(new Set(subs.data.topics));
  }, [subs.data]);
  const save = useMutation({
    mutationFn: (topics: string[]) =>
      api<KbSubscriptions>('/kb/subscriptions', { method: 'PUT', body: { topics } }),
    onSuccess: (d) => {
      qc.setQueryData(['kb', 'subs'], d);
      toast('Подписки сохранены');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось сохранить', 'danger'),
  });
  const read = useMutation({
    mutationFn: () => api('/kb/events/read', { method: 'POST', body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb', 'events'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
  if (subs.isPending) return <Spinner />;
  if (subs.error) return <ErrorBox error={subs.error} />;
  const toggle = (t: string) =>
    setOn((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  const suggested = subs.data.suggested;
  const unread = events.data?.filter((e) => !e.read).length ?? 0;
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="О чём сообщать"
        meta="сметный отдел, ГИП и ПТО подписываются на свои разделы"
        actions={
          suggested.length ? (
            <Button size="sm" onClick={() => setOn(new Set([...on, ...suggested.map((s) => s.topic)]))}>
              Подписаться на мои разделы
            </Button>
          ) : null
        }
      >
        <Group
          title="Нормативная библиотека"
          topics={[
            ['norm_all', 'Все документы библиотеки'],
            ...(Object.keys(NORM_KINDS) as Array<keyof typeof NORM_KINDS>).map((k): [string, string] => [
              `norm_kind:${k}`,
              NORM_KINDS[k],
            ]),
          ]}
          on={on}
          toggle={toggle}
        />
        <Group
          title="База знаний"
          topics={(Object.keys(KB_SECTIONS) as Array<keyof typeof KB_SECTIONS>).map((k): [string, string] => [
            `kb_section:${k}`,
            KB_SECTIONS[k],
          ])}
          on={on}
          toggle={toggle}
        />
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate([...on])}>
          Сохранить подписки
        </Button>
      </Panel>
      <Panel
        title="Что изменилось"
        meta={unread ? `новых: ${unread}` : undefined}
        actions={
          unread ? (
            <Button size="sm" onClick={() => read.mutate()}>
              Отметить прочитанным
            </Button>
          ) : null
        }
        bodyClassName="p-0"
      >
        {events.isPending ? (
          <Spinner />
        ) : events.data?.length ? (
          events.data.map((e) => (
            <Link
              key={e.id}
              to={e.path}
              className="flex min-h-11 flex-col gap-1 border-b border-line px-4 py-3 last:border-b-0 hover:bg-tile/60"
            >
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={e.read ? 'neutral' : 'accent'}>{KIND_LABEL[e.kind]}</Badge>
                <span className="text-[12px] text-ink-2">{dateTime(e.createdAt)}</span>
              </span>
              <span className="text-[14px] text-ink [overflow-wrap:anywhere]">{e.title}</span>
            </Link>
          ))
        ) : (
          <p className="px-4 py-6 text-[14px] text-ink-2">
            Пока ничего не менялось. Подпишитесь на нужные разделы выше.
          </p>
        )}
      </Panel>
    </div>
  );
}
